import type { CRBattle, CRBattlePlayer } from '@/types/qbot';

export type BattleResult = 'win' | 'loss' | 'draw';

export interface BattlePov {
  me: CRBattlePlayer;
  opp: CRBattlePlayer;
  result: BattleResult;
  won: boolean;
  draw: boolean;
}

/** '#ABC 123' | 'abc123' → 'ABC123'. Tolerates '#', spaces and case. */
function normTag(tag: string | null | undefined): string {
  return (tag ?? '').replace(/[#\s]/g, '').toUpperCase();
}

/**
 * Resolve the queried player's point of view for a battle.
 *
 * The battle log is fetched FOR `playerTag`, so the player is on `team[]` by
 * construction — but NOT necessarily at index 0 (2v2 logs carry two team
 * entries) and the tag can be missing/anonymised in some challenge and
 * tournament logs. So: look for the tag on BOTH sides at ANY index, and when
 * nothing matches fall back to `team[0]` — never to the opponent, which would
 * render an enemy as "you" (wrong deck, inverted result, inverted stats).
 * Returns null only when a side is genuinely missing.
 */
export function resolvePov(battle: CRBattle, playerTag: string): BattlePov | null {
  const wanted = normTag(playerTag);
  const team = battle.team ?? [];
  const opponent = battle.opponent ?? [];

  const inTeam = wanted ? team.find((p) => normTag(p?.tag) === wanted) : undefined;
  const inOpp =
    inTeam || !wanted ? undefined : opponent.find((p) => normTag(p?.tag) === wanted);

  // No match anywhere → the log belongs to the queried player, so `team` is us.
  const me = inTeam ?? inOpp ?? team[0];
  const opp = inOpp ? team[0] : opponent[0];
  if (!me || !opp) return null;

  const draw = me.crowns === opp.crowns;
  const won = me.crowns > opp.crowns;
  return { me, opp, result: draw ? 'draw' : won ? 'win' : 'loss', won, draw };
}
